/**
 * Track the announcement bar height in a CSS variable so layout can subtract
 * it from viewport-height calculations (e.g. the "full" slideshow max-height).
 */
(function () {
  function updateAnnouncementBarHeight() {
    const bar = document.querySelector('.announcement_bar');
    const h = bar && bar.offsetHeight ? bar.offsetHeight : 0;
    document.documentElement.style.setProperty('--announcement-bar-height', h + 'px');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateAnnouncementBarHeight);
  } else {
    updateAnnouncementBarHeight();
  }
  window.addEventListener('resize', updateAnnouncementBarHeight);

  // Re-measure when Shopify reloads a section (customizer edits, etc.)
  document.addEventListener('shopify:section:load', updateAnnouncementBarHeight);
  document.addEventListener('shopify:section:unload', updateAnnouncementBarHeight);
})();

/**
 * Sticky header: show on scroll up, hide on scroll down
 */
(function () {
  let lastScrollY = window.scrollY;
  let isHidden = false;
  let ticking = false;
  let header = null;
  const DEAD_ZONE_PX = 5;

  function onScroll() {
    if (!header) {
      header = document.querySelector('.sticky-header');
      if (!header) {
        ticking = false;
        return;
      }
    }

    const currentScrollY = window.scrollY;
    const diff = currentScrollY - lastScrollY;

    // Don't toggle while the sidebar is open (body scroll is locked anyway)
    if (document.body.classList.contains('sidebar-nav-open')) {
      ticking = false;
      return;
    }

    // Ignore sub-threshold scrolls to avoid jitter loops
    if (Math.abs(diff) < DEAD_ZONE_PX) {
      ticking = false;
      return;
    }

    if (currentScrollY <= 0) {
      if (isHidden) {
        header.classList.remove('header-hidden');
        isHidden = false;
      }
    } else if (diff > 0 && !isHidden) {
      header.classList.add('header-hidden');
      isHidden = true;
    } else if (diff < 0 && isHidden) {
      header.classList.remove('header-hidden');
      isHidden = false;
    }

    lastScrollY = currentScrollY;
    ticking = false;
  }

  window.addEventListener('scroll', function () {
    if (!ticking) {
      requestAnimationFrame(onScroll);
      ticking = true;
    }
  });
})();

/**
 * Curtain-left sidebar navigation
 * Only runs when Header setting "Dropdown curtain position" is "left".
 * Neutralises the theme's hover-to-open and makes top-level items toggle
 * a left-sliding drawer on click.
 */
(function () {
  /** @type {Array<{ li: HTMLElement, content: HTMLElement, cloneItems: HTMLElement[] }>} */
  const pairs = [];
  let drawer = null;         // the single shared drawer element
  let contentSlot = null;    // where active item's dropdown markup lives
  let activePair = null;

  function setCloneActive(pair) {
    pairs.forEach(function (p) {
      p.cloneItems.forEach(function (item) {
        if (p === pair) item.classList.add('is-active');
        else item.classList.remove('is-active');
      });
    });
  }

  function closeAll() {
    if (drawer) drawer.classList.remove('sidebar-active');
    document.body.classList.remove('sidebar-nav-open');
    setCloneActive(null);
    activePair = null;
  }

  function syncClonePositionToRealNav() {
    if (!drawer) return;
    const realNav = document.querySelector('.site-header .site-nav:not(.curtain-left-nav-clone)');
    if (!realNav) return;
    const rect = realNav.getBoundingClientRect();
    drawer.style.setProperty('--curtain-left-pad-top', Math.max(0, rect.top) + 'px');
    drawer.style.setProperty('--curtain-left-pad-left', Math.max(0, rect.left) + 'px');
  }

  function openPair(pair) {
    // Swap content if different — no transition side effects because the
    // drawer element itself is the same across menu items.
    if (activePair !== pair) {
      contentSlot.replaceChildren(pair.content);
      setCloneActive(pair);
      activePair = pair;
    }
    syncClonePositionToRealNav();
    drawer.classList.add('sidebar-active');
    document.body.classList.add('sidebar-nav-open');
  }

  function init() {
    const siteHeader = document.querySelector('.site-header[data-curtain-position="left"]');
    if (!siteHeader) return;

    const parents = siteHeader.querySelectorAll('.site-nav > .site-nav--has-dropdown');
    if (!parents.length) return;

    // 1. Block the theme Menu class from reacting to hover/touch/focus.
    const blockedEvents = ['mouseover', 'mouseenter', 'mouseleave', 'mouseout', 'touchstart', 'focusin', 'focusout'];
    parents.forEach(function (li) {
      blockedEvents.forEach(function (evtName) {
        li.addEventListener(evtName, function (evt) { evt.stopImmediatePropagation(); }, true);
      });
    });

    // 2. Detach each dropdown from the header and remember it by parent <li>.
    parents.forEach(function (li) {
      const dropdown = li.querySelector(':scope > .site-nav__dropdown');
      if (!dropdown) return;
      dropdown.parentNode.removeChild(dropdown);
      pairs.push({ li: li, content: dropdown, cloneItems: [] });
    });

    if (!pairs.length) return;

    // 3. Build the single shared drawer.
    drawer = document.createElement('div');
    drawer.className = 'curtain-left-drawer';
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.appendChild(drawer);

    // Cloned top-level nav (persistent, lives once in the drawer).
    const navClone = document.createElement('ul');
    navClone.className = 'site-nav curtain-left-nav-clone';
    pairs.forEach(function (pair) {
      const li = document.createElement('li');
      li.className = 'first-level-dropdown curtain-left-nav-clone__item';

      const srcLink = pair.li.querySelector(':scope > a.site-nav__link');
      const linkClone = srcLink.cloneNode(true);
      linkClone.setAttribute('href', '#');
      linkClone.addEventListener('click', function (evt) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        if (activePair === pair) {
          closeAll();
        } else {
          openPair(pair);
        }
      });

      li.appendChild(linkClone);
      navClone.appendChild(li);
      pair.cloneItems.push(li);
    });
    drawer.appendChild(navClone);

    // Slot that holds the active item's dropdown markup.
    contentSlot = document.createElement('div');
    contentSlot.className = 'curtain-left-drawer__content';
    drawer.appendChild(contentSlot);

    // 4. Top-level anchors in the real header toggle the drawer.
    pairs.forEach(function (pair) {
      const link = pair.li.querySelector(':scope > a.site-nav__link');
      if (!link) return;

      link.addEventListener('click', function (evt) {
        evt.preventDefault();
        evt.stopImmediatePropagation();

        if (activePair === pair) {
          closeAll();
        } else {
          openPair(pair);
        }
      }, true);
    });

    // 5. Clicking any link inside the drawer closes it so navigation proceeds.
    drawer.addEventListener('click', function (evt) {
      const anchor = evt.target.closest('a[href]');
      if (anchor && anchor.getAttribute('href') !== '#' && drawer.contains(anchor)) {
        closeAll();
      }
    });

    // 6. Move the backdrop to body and wire up close-on-click.
    const backdrop = document.querySelector('[data-sidebar-nav-backdrop]');
    if (backdrop) {
      if (backdrop.parentElement !== document.body) {
        document.body.appendChild(backdrop);
      }
      backdrop.addEventListener('click', closeAll);
    }

    // 7. Close on Escape.
    document.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape' && document.body.classList.contains('sidebar-nav-open')) {
        closeAll();
      }
    });

    // 8. Re-sync the cloned-nav position when the window resizes while the
    //    drawer is open.
    window.addEventListener('resize', function () {
      if (document.body.classList.contains('sidebar-nav-open')) {
        syncClonePositionToRealNav();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
